import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Authorizations } from "~/api/types";
import { routes } from "~/router";

import {
  Keyword,
  KeywordBulkAttributes,
  Keywords,
  Regions,
  Site,
} from "../../api/Api";
import { apiClient } from "../../api/ApiClient";
import KeywordSuggestions from "../keywordSuggestions/KeywordSuggestions";
import Card from "../ui/card/Card";
import Modal from "../ui/modal/Modal";
import { Pagination, PaginationConfig } from "../ui/pagination/Pagination";
import FocusKeyword from "./FocusKeyword";
import styles from "./KeywordList.module.scss";
import { KeywordColumnSort, KeywordTable } from "./KeywordTable";

type KeywordListProps = {
  keywords: Keywords;
  pagination: PaginationConfig;
  setPagination: (pagination: PaginationConfig) => void;
  isLoading: boolean;
  regions: Regions;
  focusKeywordDetail: null | Keyword;
  isLoadingFocusKeywordDetail: boolean;
  isKeywordsFetching: boolean;
  setSearchTerm: (searchTerm: string) => void;
  searchTerm: string;
  site: Site | null | undefined;
  authorizations: Authorizations;
  refetch: () => void;
  sorting: KeywordColumnSort[];
  onSortingChange: (sorting: KeywordColumnSort[]) => void;
};

export const KeywordList = ({
  keywords,
  pagination,
  setPagination,
  isLoading,
  regions,
  focusKeywordDetail,
  isLoadingFocusKeywordDetail,
  isKeywordsFetching,
  searchTerm,
  setSearchTerm,
  site,
  authorizations,
  refetch,
  sorting,
  onSortingChange,
}: KeywordListProps) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);

  if (!id) {
    throw Error("No site id provided");
  }

  const { mutate: createKeywords, isPending: isPendingCreateKeywords } =
    useMutation({
      mutationFn: async (data: KeywordBulkAttributes) => {
        const payload = {
          names: selectedKeywords,
          keyword_attributes: data,
        };

        return await apiClient.sites.keywordImportsCreate(Number(id), payload);
      },
      onSuccess: (response) => {
        const importId = response.data.keyword_import.id;
        enqueueSnackbar(`Keywords were added successfully!`, {
          variant: "success",
        });
        closeModal();
        navigate(routes.site.keywords.import({ id, importId }));
        queryClient.invalidateQueries({ queryKey: ["keywordSuggestions"] });
        queryClient.invalidateQueries({ queryKey: ["keywordImports"] });
        refetch();
      },
      onError: (error) => {
        // @ts-expect-error from responce
        const errorMessage = error?.error?.error || "Error";
        enqueueSnackbar(errorMessage, {
          variant: "error",
          persist: true,
        });
      },
    });

  const openModal = () => {
    setSelectedKeywords([]);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedKeywords([]);
  };

  return (
    <div className={styles.container}>
      <Card title={`Focus keyword: ${focusKeywordDetail?.name || "N/A"}`}>
        <FocusKeyword
          data={focusKeywordDetail}
          isLoadingFocusKeywordDetail={isLoadingFocusKeywordDetail}
          isLoading={isLoading}
        />
      </Card>
      <Card title="All Keywords">
        <KeywordTable
          site={site}
          authorizations={authorizations}
          keywords={keywords}
          isLoading={isLoading}
          regions={regions}
          openModal={openModal}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          refetch={refetch}
          isRefetching={isKeywordsFetching}
          sorting={sorting}
          onSortingChange={onSortingChange}
        />

        <Pagination
          {...pagination}
          isLoading={isKeywordsFetching}
          totalItems={pagination.totalItems}
          onPageChange={(page) => {
            setPagination({ ...pagination, currentPage: page });
            setTimeout(() => refetch(), 10);
          }}
          onItemsPerPageChange={(items) => {
            setPagination({
              ...pagination,
              itemsPerPage: items,
              currentPage: 1,
            });
            setTimeout(() => refetch(), 10);
          }}
        />
      </Card>

      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          closeModal={closeModal}
          isLoading={isPendingCreateKeywords}
          buttonLabel="Add Keywords"
          formId="keywordSuggestions"
          width="75rem"
          // @ts-expect-error TS2339 non-existing className
          modalLabel={<h3 className={styles.modalTitle}>Add New Keywords</h3>}
        >
          <KeywordSuggestions
            selectedKeywords={selectedKeywords}
            setSelectedKeywords={setSelectedKeywords}
            siteId={Number(id)}
            withAttributes={true}
            // @ts-expect-error BE types
            createKeywords={createKeywords}
            refetch={refetch}
          />
        </Modal>
      )}
    </div>
  );
};
